/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Calls Routing module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../config/api';

// Re-export types from types module (avoiding conflicts with api/gatewayApi.ts)
// These types are also exported from api/gatewayApi.ts, so we don't re-export them here
// to avoid TypeScript conflicts

export const GATEWAYS_ENDPOINT = '/api/gateways';
export const GATEWAY_SIP_PROFILES_ENDPOINT = '/api/telephony/gateway-sip-profiles';
export const SIP_PROFILES_ENDPOINT = '/api/gateways/sip-profiles';

export interface GatewayDomain {
  id: string | number;
  domain: string;
}

export interface GatewaySipProfile {
  id: number;
  name: string;
  description?: string;
}

export interface GatewaySetting {
  name: string;
  value: string | number | boolean;
}

export interface Gateway {
  id: string | number;
  uuid: string;
  name: string;
  description?: string;
  username?: string;
  proxy?: string;
  enabled: boolean;
  register: boolean;
  realm?: string;
  gatewaySipProfileId?: number | null;
  domain?: GatewayDomain;
  sipProfile?: {
    id: string | number;
    name: string;
  };
  createdAt: string;
  updatedAt?: string;
  settings?: GatewaySetting[];
}

export interface ApiPagination {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  pagination?: ApiPagination;
}

export interface ApiErrorResponse {
  success: false;
  message?: string;
  [key: string]: unknown;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse | null | undefined;

const normaliseHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
};

export async function requestGatewaysApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<ApiSuccessResponse<T>> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normaliseHeaders(options.headers)
      }
    }
  );

  let payload: ApiResponse<T>;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && payload.success) {
    return payload as ApiSuccessResponse<T>;
  }

  return {
    success: true,
    data: undefined as T
  };
}


